import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { useRef, useState } from "react";

const testimonials = [
  {
    name: "Adeola Johnson",
    role: "Trader, Lagos",
    country: "🇳🇬",
    text: "BUYFIAT has transformed how I trade crypto. The instant settlements and low fees are game-changers for my business.",
    rating: 5,
  },
  {
    name: "Kwame Asante",
    role: "Business Owner, Accra",
    country: "🇬🇭",
    text: "The P2P marketplace is incredibly secure. I have traded over $50,000 without a single issue. Highly recommended!",
    rating: 5,
  },
  {
    name: "Sarah Kimani",
    role: "Freelancer, Nairobi",
    country: "🇰🇪",
    text: "M-Pesa integration makes deposits and withdrawals seamless. Best crypto platform for East Africa!",
    rating: 5,
  },
  {
    name: "Pieter van der Merwe",
    role: "Investor, Cape Town",
    country: "🇿🇦",
    text: "Professional platform with excellent customer support. The analytics tools help me make informed decisions.",
    rating: 5,
  },
];

type Testimonial = (typeof testimonials)[number];

type VerticalMarqueeProps = {
  testimonials: Testimonial[];
  direction: "up" | "down";
  duration: number;
  activeTestimonial: number;
  onSelect: (index: number) => void;
  startIndex?: number;
};

const VerticalMarquee = ({
  testimonials,
  direction,
  duration,
  activeTestimonial,
  onSelect,
  startIndex = 0,
}: VerticalMarqueeProps) => {
  // Duplicate the list so the marquee can loop continuously.
  const items = [...testimonials, ...testimonials];

  return (
    <div className="relative h-full overflow-hidden rounded-2xl">
      {/* Top fade */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-20 bg-gradient-to-b from-background to-transparent" />

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-20 bg-gradient-to-t from-background to-transparent" />

      <motion.div
        className="flex flex-col gap-6"
        animate={
          direction === "up" ? { y: ["0%", "-50%"] } : { y: ["-50%", "0%"] }
        }
        transition={{
          duration,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }}
        whileHover={{ animationPlayState: "paused" }}
      >
        {items.map((testimonial, index) => {
          const originalIndex = index % testimonials.length;
          const globalIndex = startIndex + originalIndex;

          return (
            <motion.div
              key={`${testimonial.name}-${index}`}
              layout
              className="flex-shrink-0"
              onClick={() => onSelect(globalIndex)}
            >
              <TestimonialCard
                testimonial={testimonial}
                active={activeTestimonial === globalIndex}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

const TestimonialCard = ({
  testimonial,
  active,
}: {
  testimonial: Testimonial;
  active: boolean;
}) => {
  return (
    <div
      className={cn(
        "testimonial-card nex-card group flex min-h-[260px] flex-col rounded-2xl border p-6",
        "border-border/60 bg-card/90 backdrop-blur-sm",
        "transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
        active && "border-primary/50 shadow-md shadow-primary/5",
      )}
    >
      {/* Rating */}
      <div
        className="mb-4 flex items-center gap-1"
        aria-label={`${testimonial.rating} out of 5 stars`}
      >
        {Array.from({ length: testimonial.rating }, (_, index) => (
          <Star
            key={index}
            className="h-4 w-4 fill-primary text-primary"
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Testimonial text */}
      <blockquote className="mb-6 flex-1 text-sm leading-relaxed text-muted-foreground">
        “{testimonial.text}”
      </blockquote>

      {/* Customer details */}
      <div className="mt-auto flex items-center gap-3 border-t border-border/60 pt-4">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-gradient-to-br from-primary/15 to-primary/5 text-2xl"
          aria-hidden="true"
        >
          {testimonial.country}
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {testimonial.name}
          </p>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {testimonial.role}
          </p>
        </div>
      </div>
    </div>
  );
};

const BuyFiatTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const testimonialsRef = useRef<HTMLElement | null>(null);

  const selectTestimonial = (index: number) => {
    setActiveTestimonial(index);
  };

  // Distribute testimonials across columns without hardcoding card content.
  const splitIntoColumns = (columnCount: number) =>
    Array.from({ length: columnCount }, (_, columnIndex) =>
      testimonials.filter(
        (_, testimonialIndex) => testimonialIndex % columnCount === columnIndex,
      ),
    );

  const desktopColumns = splitIntoColumns(4);
  const tabletColumns = splitIntoColumns(2);

  return (
    <section
      ref={testimonialsRef}
      className="overflow-hidden bg-muted/30 py-20 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-12 text-center sm:mb-16"
        >
          <span className="mb-4 block text-sm font-medium tracking-wider text-primary">
            TESTIMONIALS
          </span>

          <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
            Trusted by{" "}
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Thousands
            </span>
          </h2>

          <p className="mx-auto max-w-xl text-base text-muted-foreground sm:text-lg">
            See what our users say about trading on BUYFIAT.
          </p>
        </motion.div>

        {/* Desktop: four animated columns */}
        <div className="hidden h-[720px] grid-cols-4 gap-6 lg:grid">
          {desktopColumns.map((column, columnIndex) => (
            <VerticalMarquee
              key={`desktop-${columnIndex}`}
              testimonials={column}
              direction={columnIndex % 2 === 0 ? "up" : "down"}
              duration={38 + columnIndex * 3}
              activeTestimonial={activeTestimonial}
              onSelect={selectTestimonial}
              startIndex={columnIndex}
            />
          ))}
        </div>

        {/* Tablet: two animated columns */}
        <div className="hidden h-[680px] grid-cols-2 gap-6 md:grid lg:hidden">
          {tabletColumns.map((column, columnIndex) => (
            <VerticalMarquee
              key={`tablet-${columnIndex}`}
              testimonials={column}
              direction={columnIndex % 2 === 0 ? "up" : "down"}
              duration={40 + columnIndex * 5}
              activeTestimonial={activeTestimonial}
              onSelect={selectTestimonial}
              startIndex={columnIndex}
            />
          ))}
        </div>

        {/* Mobile: one animated column */}
        <div className="grid h-[600px] grid-cols-1 md:hidden">
          <VerticalMarquee
            testimonials={testimonials}
            direction="up"
            duration={42}
            activeTestimonial={activeTestimonial}
            onSelect={selectTestimonial}
          />
        </div>

        {/* Navigation dots */}
        <div
          className="mt-8 flex justify-center gap-2"
          aria-label="Select a testimonial"
        >
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.name}
              type="button"
              onClick={() => setActiveTestimonial(index)}
              aria-label={`Select testimonial from ${testimonial.name}`}
              aria-pressed={activeTestimonial === index}
              className={cn(
                "h-2.5 rounded-full transition-all duration-300",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                activeTestimonial === index
                  ? "w-7 bg-primary"
                  : "w-2.5 bg-border hover:bg-primary/50",
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BuyFiatTestimonials;
